import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LocaleService } from '@next/core-lib/i18n';
import { RelationshipsActions } from '@next/core-lib/relationships';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { combineLatest, map, shareReplay, takeUntil, tap, Subject } from 'rxjs';

import { ManureRequestForm } from './models/manure-request.model';
import {
  commaSeparatedDecimalValidator,
  convertToNumber,
} from '../shared/decimal-input-validation';
import { ManureCategory } from '../shared/models/manure-category.model';
import { manureTypeOptions } from '../shared/models/manure-type.model';
import { Month } from '../shared/models/month.model';
import { StorageForm } from '../shared/models/storage-form.model';
import { TransportType } from '../shared/models/transport-type.model';
import * as requestActions from './store/actions/request.actions';
import { IntermediaryService } from '../shared/services/intermediary.service';

@Component({
  templateUrl: './request.component.html',
  styleUrls: ['./request.component.scss'],
  standalone: false,
})
export class RequestComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly location = inject(Location);
  private readonly intermediaryService = inject(IntermediaryService);
  private readonly localeService = inject(LocaleService);
  private readonly destroy$ = new Subject<void>();

  pageTitle$ = this.store.select(SiteSelectors.selectPageTitle);

  formGroupInitialState = {
    manureType: new FormControl(undefined, [Validators.required]),
    manureCategory: new FormControl(undefined, [Validators.required]),
    amountInTons: new FormControl(undefined, [
      Validators.required,
      Validators.min(35),
      Validators.pattern('[0-9]+'),
    ]),
    pricePerTon: new FormControl(undefined, [Validators.required]),
    paymentType: new FormControl(undefined, [Validators.required]),
    desiredDeliveryPeriodFrom: new FormControl(undefined),
    desiredDeliveryPeriodTo: new FormControl(undefined),
    nKgPerTon: new FormControl<string | undefined>(undefined, [commaSeparatedDecimalValidator]),
    pKgPerTon: new FormControl<string | undefined>(undefined, [commaSeparatedDecimalValidator]),
    kKgPerTon: new FormControl<string | undefined>(undefined, [commaSeparatedDecimalValidator]),
    transportType: new FormControl(undefined),
    preferredIntermediary: new FormControl(undefined, [Validators.required]),
    storageForm: new FormControl(undefined),
    isHomogeneous: new FormControl(undefined),
  };
  formGroup: FormGroup = new FormGroup(this.formGroupInitialState);

  selectedManureCategory$ = this.formGroup.controls.manureCategory.valueChanges.pipe(
    tap((manureCategory: ManureCategory) => {
      this.formGroup.controls.manureType.setValue(undefined);
      this.formGroup.controls.manureType.updateValueAndValidity();
      if (manureCategory === ManureCategory.solid) {
        this.formGroup.controls.transportType.setValue(TransportType.walkingFloor);
        this.formGroup.controls.manureType.updateValueAndValidity();
      } else {
        this.formGroup.controls.transportType.setValue(undefined);
        this.formGroup.controls.transportType.clearValidators();
        this.formGroup.controls.transportType.updateValueAndValidity();
      }
    }),
    shareReplay(1),
  );
  selectedLocation$ = this.store.pipe(select(UserSelectors.selectLocation));
  manureTypes$ = combineLatest([this.selectedManureCategory$]).pipe(
    map(([manureCategory]: [ManureCategory]) => manureTypeOptions[manureCategory]),
  );
  showTransportType$ = this.selectedManureCategory$.pipe(
    map((x: ManureCategory) => x === ManureCategory.solid),
  );
  totalPrice$ = combineLatest([
    this.formGroup.controls.amountInTons.valueChanges,
    this.formGroup.controls.pricePerTon.valueChanges,
  ]).pipe(map(([amountInTons, pricePerTon]: [number, number]) => amountInTons * pricePerTon));
  totalN$ = combineLatest([
    this.formGroup.controls.amountInTons.valueChanges,
    this.formGroup.controls.nKgPerTon.valueChanges,
  ]).pipe(
    map(([amountInTons, nKgPerTon]: [number, string]) => amountInTons * convertToNumber(nKgPerTon)),
  );
  totalK$ = combineLatest([
    this.formGroup.controls.amountInTons.valueChanges,
    this.formGroup.controls.kKgPerTon.valueChanges,
  ]).pipe(
    map(([amountInTons, kKgPerTon]: [number, string]) => amountInTons * convertToNumber(kKgPerTon)),
  );
  totalP$ = combineLatest([
    this.formGroup.controls.amountInTons.valueChanges,
    this.formGroup.controls.pKgPerTon.valueChanges,
  ]).pipe(
    map(([amountInTons, pKgPerTon]: [number, string]) => amountInTons * convertToNumber(pKgPerTon)),
  );

  manureCategories = Object.values(ManureCategory);
  transportTypes = Object.values(TransportType);
  storageForms = Object.values(StorageForm);
  months = Object.values(Month);
  intermediaries = signal<string[]>([]);

  ngOnInit() {
    this.intermediaryService
      .getIntermediaries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((intermediaries) => this.intermediaries.set(intermediaries));

    this.store.dispatch(
      SiteActions.updatePageTitle({
        pageTitle: this.localeService.translate('dashboard.page-title'),
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  back() {
    this.location.back();
  }

  openLocationsDialog() {
    this.store.dispatch(RelationshipsActions.openLocationsDialog());
  }

  submit(request: ManureRequestForm) {
    this.store.dispatch(
      requestActions.sendManureRequest({
        request: {
          ...request,
          nKgPerTon: convertToNumber(request.nKgPerTon),
          pKgPerTon: convertToNumber(request.pKgPerTon),
          kKgPerTon: convertToNumber(request.kKgPerTon),
        },
      }),
    );
  }
}
